import React from 'react';

/**
 * Spinner component props interface
 */
export interface SpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Color variant of the spinner */
  color?: 'primary' | 'secondary' | 'white' | 'current';
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** Accessible label for screen readers */
  label?: string;
}

/**
 * Design system colors
 */
const COLORS = {
  primary: 'var(--primary-base)',
  secondary: 'var(--text-low_em)',
  white: 'var(--surface-surface_0)',
  current: 'currentColor',
} as const;

/**
 * Spinner component following design system standards
 * Provides loading indication with various sizes and colors
 * 
 * @example
 * ```tsx
 * <Spinner size="md" color="primary" />
 * 
 * <Spinner size="lg" color="white" label="Loading content..." />
 * ```
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className,
  style,
  label = 'Loading...',
}) => {
  const getSpinnerStyles = (): React.CSSProperties => {
    const sizeStyles: Record<NonNullable<SpinnerProps['size']>, React.CSSProperties> = {
      sm: { width: '16px', height: '16px' },
      md: { width: '24px', height: '24px' },
      lg: { width: '32px', height: '32px' },
      xl: { width: '48px', height: '48px' },
    };

    const baseStyles: React.CSSProperties = {
      animation: 'spin 1s linear infinite',
      color: COLORS[color],
      display: 'inline-block',
    };

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...style,
    };
  };

  return (
    <>
      {/* Add keyframes for spin animation if not already present */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      
      <svg
        style={getSpinnerStyles()}
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        role="status"
        aria-label={label}
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          style={{ opacity: 0.25 }}
        />
        <path
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          style={{ opacity: 0.75 }}
        />
      </svg>
      
      {/* Screen reader only text */}
      <span style={{ 
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}>
        {label}
      </span>
    </>
  );
};

Spinner.displayName = 'Spinner';

export default Spinner;
